import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Card } from '@/components';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks';

interface OnboardingFormData {
  shopName: string;
  ownerName: string;
  address: string;
  whatsappNumber: string;
  referralCode: string;
}

export const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { admin } = useAuth();
  
  const [formData, setFormData] = useState<OnboardingFormData>({
    shopName: '',
    ownerName: '',
    address: '',
    whatsappNumber: '',
    referralCode: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name] || errors.submit) {
      setErrors(prev => ({ ...prev, [name]: '', submit: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.shopName.trim()) newErrors.shopName = 'Shop name is required';
    if (!formData.ownerName.trim()) newErrors.ownerName = 'Owner name is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    const cleanedPhone = formData.whatsappNumber.trim().replace(/[\s-]/g, '');
    if (!cleanedPhone) {
      newErrors.whatsappNumber = 'WhatsApp number is required';
    } else if (!phoneRegex.test(cleanedPhone)) {
      newErrors.whatsappNumber = 'Invalid phone number (must include country code)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !admin?.shop_id) return;

    setIsLoading(true);
    try {
      // We need to fetch the existing shop first so we don't overwrite other shop_details
      const { data: shop } = await supabase
        .from('shops')
        .select('shop_details')
        .eq('id', admin.shop_id)
        .single();
        
      const existingDetails = shop?.shop_details || {};

      const { error } = await supabase
        .from('shops')
        .update({
          name: formData.shopName.trim(),
          whatsapp_phone_number: formData.whatsappNumber.trim().replace(/[\s-]/g, ''),
          shop_details: {
            ...existingDetails,
            owner_name: formData.ownerName.trim(),
            address: formData.address.trim(),
            referral_code: formData.referralCode.trim(),
          },
          is_onboarded: true
        })
        .eq('id', admin.shop_id);

      if (error) throw error;

      // Force a reload so useAuth refetches the newly onboarded state
      window.location.href = '/dashboard';
    } catch (error: any) {
      if (error.message?.includes('shops_whatsapp_phone_number_key')) {
        setErrors({ 
          whatsappNumber: 'This WhatsApp number is already registered to another shop. Please use a different number.' 
        });
      } else {
        setErrors({ submit: error.message || 'Failed to save shop details' });
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <div className="space-y-6">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-slate-900">Almost there!</h1>
            <p className="text-slate-600 mt-2 text-sm leading-relaxed">
              Please provide a few more details about your shop to complete your account setup.
            </p>
          </div>

          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg" role="alert">
              <p className="text-red-800 text-xs">✕ {errors.submit}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              id="shopName"
              name="shopName"
              label="Shop Name"
              placeholder="PrintNow Inc."
              value={formData.shopName}
              onChange={handleChange}
              error={errors.shopName}
              disabled={isLoading}
              fullWidth
            />

            <Input
              id="ownerName"
              name="ownerName"
              label="Owner Name"
              placeholder="Rahul Kumar"
              value={formData.ownerName}
              onChange={handleChange}
              error={errors.ownerName}
              disabled={isLoading}
              fullWidth
            />

            <Input
              id="address"
              name="address"
              label="Shop Address"
              placeholder="123 MG Road, Mumbai, Maharashtra"
              value={formData.address}
              onChange={handleChange}
              error={errors.address}
              disabled={isLoading}
              fullWidth
            />

            <Input
              id="whatsappNumber"
              name="whatsappNumber"
              label="WhatsApp Number (with country code)"
              placeholder="+91 98765 43210"
              value={formData.whatsappNumber}
              onChange={handleChange}
              error={errors.whatsappNumber}
              disabled={isLoading}
              fullWidth
            />

            <Input
              id="referralCode"
              name="referralCode"
              label="Referral Code (Optional)"
              placeholder="REF123"
              value={formData.referralCode}
              onChange={handleChange}
              error={errors.referralCode}
              disabled={isLoading}
              fullWidth
            />

            <div className="pt-2">
              <Button
                type="submit"
                variant="primary"
                size="md"
                fullWidth
                isLoading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Complete Setup'}
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
};
